import { useCallback, useEffect, useRef, useState } from 'react';

import { getStoredOrderId } from 'experiments/checkout-v2/control/pages/cart/payment/order-payment/util';
import {
  DeliveryStatus,
  IPriceDeliveryInput,
  IPriceOrderInput,
  usePriceOrderMutation,
} from 'generated/graphql-gateway';
import usePosVendor from 'hooks/menu/use-pos-vendor';
import { useOrderStatus } from 'hooks/order-status';
import { useCart } from 'hooks/use-cart';
import useIsSignUpAfterCartEnabled from 'hooks/use-is-sign-up-after-cart';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useOrderContext } from 'state/order';
import { OrderStatus } from 'state/order/types';
import { buildPriceDeliveryInput, buildPriceOrderInput } from 'utils/cart';
import { computeOtherDiscountAmount } from 'utils/cart/helper';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import { logger } from 'utils/logger';
import { TraceName, startTrace, stopTrace } from 'utils/performance/trace';
import { isDelivery } from 'utils/service-mode';

import { IPriceOrderParams, IUsePriceOrderParams } from './types';

// default order status polling interval, in ms
const DEFAULT_POLL_INTERVAL = 1000;
const PRICING_RETRY_LIMIT = 2; // Attempt reprice 3 times

const failureStatuses = {
  delivery: [DeliveryStatus.QUOTE_ERROR, DeliveryStatus.QUOTE_UNAVAILABLE],
  pos: [OrderStatus.PRICE_ERROR],
};

// note that serverOrder contains statuses for commit and price order, we have to make sure that
// the statuses we check here belongs to pricing errors statuses
export const priceOrderErrorStatuses = [...failureStatuses.delivery, ...failureStatuses.pos];

export const usePriceOrder = ({
  cartEntries,
  orderStatusPollInterval = DEFAULT_POLL_INTERVAL,
  updateTipAmount,
}: IUsePriceOrderParams) => {
  const { calculateCartTotalWithDiscount } = useCart();
  const { quoteId } = useOrderContext();
  const isSignUpAfterCart = useIsSignUpAfterCartEnabled();
  const [fireMutation, mutationResult] = usePriceOrderMutation({
    context: { shouldNotBatch: true },
  });
  const enablePaymentErrorContinue = useFlag(LaunchDarklyFlag.ENABLE_PAYMENT_ERROR_CONTINUE);
  const { vendor } = usePosVendor();
  const [didOrderPricingRetryFail, setDidOrderPricingRetryFail] = useState(false);
  const orderPricingAttemptsRef = useRef<number>(0);

  const successStatuses = enablePaymentErrorContinue
    ? [
        OrderStatus.PRICE_SUCCESSFUL,
        // This will prevent this price order query from being called again
        // if the order is already inserted
        OrderStatus.INSERT_SUCCESSFUL,
        OrderStatus.PAYMENT_ERROR,
        OrderStatus.VALIDATION_ERROR,
      ]
    : [OrderStatus.PRICE_SUCCESSFUL, OrderStatus.VALIDATION_ERROR];

  const { isCartTotalNegative = false } = calculateCartTotalWithDiscount?.() || {};

  const existingOrder = getStoredOrderId();
  const rbiOrderId = mutationResult.data?.priceOrder?.rbiOrderId ?? existingOrder ?? null;

  const orderStatusQueryResult = useOrderStatus({
    failureStatuses,
    onSuccess(rbiOrder) {
      if (isDelivery(rbiOrder.cart.serviceMode)) {
        const otherDiscountAmount = computeOtherDiscountAmount(rbiOrder.cart.discounts);
        if (updateTipAmount) {
          updateTipAmount({
            subTotalCents: rbiOrder.cart.subTotalCents,
            otherDiscountAmount,
          });
        }
      }
      stopTrace(TraceName.PRICE_ORDER, { rbiOrderId });
    },
    orderStatusPollInterval,
    rbiOrderId,
    successStatuses: {
      delivery: DeliveryStatus.QUOTE_SUCCESSFUL,
      pos: successStatuses,
    },
    skip: !rbiOrderId && isCartTotalNegative && mutationResult.loading,
  });

  const {
    orderStatus,
    serverOrder,
    failure: priceOrderFailure,
    success: priceOrderSuccess,
    isPricingFailureRecoverable,
    isLoading,
  } = orderStatusQueryResult;

  const isDeliveryOrderLoading =
    isDelivery(serverOrder?.cart.serviceMode) && !serverOrder?.delivery;

  // priceOrderFailure && isPricingFailureRecoverable toggle during repricing,
  // so we check orderPricingAttemptsRef > 0 to maintain a stable true value
  // throughout the retry process, preventing UI flicker in dependent components.
  const isOrderPricingRetryInProgress =
    Boolean(serverOrder?.status && priceOrderErrorStatuses.includes(serverOrder.status)) &&
    ((priceOrderFailure && isPricingFailureRecoverable) || orderPricingAttemptsRef.current > 0) &&
    !didOrderPricingRetryFail;

  // serverOrder?.rbiOrderId !== rbiOrderId check covers the brief moment during
  // which the mutation finished but the order status query hasn't yet started.
  // Without this, `loading` is set to `false` momentarily, which causes the UI
  // to become enabled for that brief period only to then become disabled when
  // the order status query begins.
  const loading =
    mutationResult.loading ||
    (serverOrder?.rbiOrderId || null) !== (rbiOrderId || null) ||
    isLoading ||
    isDeliveryOrderLoading;

  // Note that this actually just requests a price order. the result of this process is a polling we
  // do in the `useOrderStatus` hook.
  const priceOrder = useCallback(
    (options: Omit<IPriceOrderParams, 'cartEntries' | 'redeemReward'>) => {
      const isCartInvalid = !cartEntries.length || isCartTotalNegative;

      if (isCartInvalid || isSignUpAfterCart) {
        return;
      }

      const input = buildPriceOrderInput({ cartEntries, vendor, options });

      if (!input) {
        return;
      }

      const delivery = buildPriceDeliveryInput(options, quoteId);
      const mutationVariables = { input, delivery };

      LocalStorage.setItem(StorageKeys.PRICE_ORDER_INPUT, mutationVariables);

      startTrace(TraceName.PRICE_ORDER);

      return fireMutation({
        variables: mutationVariables,
      });
    },
    [cartEntries, isSignUpAfterCart, isCartTotalNegative, vendor, quoteId, fireMutation]
  );

  useEffect(() => {
    if (priceOrderFailure) {
      if (!priceOrderErrorStatuses.includes(serverOrder.status)) {
        return;
      }

      const isMaxRetryLimitReached = orderPricingAttemptsRef.current === PRICING_RETRY_LIMIT;

      if (isMaxRetryLimitReached) {
        setDidOrderPricingRetryFail(true);
        return;
      }

      const mutationVariables = LocalStorage.getItem<{
        input: IPriceOrderInput;
        delivery: IPriceDeliveryInput;
      }>(StorageKeys.PRICE_ORDER_INPUT);

      if (mutationVariables === null) {
        logger.error('Price order mutation variables are null, cannot retry pricing.');
        return;
      }

      orderPricingAttemptsRef.current += 1;

      startTrace(TraceName.PRICE_ORDER);

      fireMutation({
        variables: mutationVariables,
      });
    }
  }, [
    priceOrderFailure,
    isPricingFailureRecoverable,
    isOrderPricingRetryInProgress,
    fireMutation,
    serverOrder,
  ]);

  const setIsRepricing = useCallback(() => {
    setDidOrderPricingRetryFail(false);
    orderPricingAttemptsRef.current = 0;
  }, [setDidOrderPricingRetryFail]);

  return {
    loading,
    orderStatus,
    priceOrderFailure,
    priceOrderSuccess,
    priceResult: mutationResult,
    rbiOrderId,
    serverOrder,
    priceOrder,
    isOrderPricingRetryInProgress,
    isPricingFailureRecoverable,
    didOrderPricingRetryFail,
    setIsRepricing,
  } as const;
};
